/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../../../_metronic/assets/ts/_utils'
import {useThemeMode} from '../../../../../_metronic/partials'
import {IsalesPerPeriod} from '../../utils/parseDashboard'
import {formatMonetaryToBr} from '../../../../../utils/formatMonetaryToBr'
import {getChartColor} from './ChartColors'

type ChartsSalesPerPeriodProps = {
  className: string
  salesPerPeriod: IsalesPerPeriod
  totalPriceSalesPerPeriod: number
}

const ChartSalesPerPeriod: React.FC<ChartsSalesPerPeriodProps> = ({
  className,
  salesPerPeriod,
  totalPriceSalesPerPeriod,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()

  const refreshMode = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, salesPerPeriod.categories, salesPerPeriod.series)
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshMode()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, salesPerPeriod])

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header pt-5'>
        <div className='card-title d-flex flex-column'>
          <div className='d-flex align-items-center'>
            <span className='fs-2hx fw-bold text-dark me-2 lh-1 ls-n2'>
              {formatMonetaryToBr(totalPriceSalesPerPeriod)}
            </span>
          </div>
          <span className='text-gray-400 pt-1 fw-semibold fs-6'>Valor vendido no período</span>
        </div>
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body'>
        {/* begin::Chart */}
        <div ref={chartRef} id='kt_charts_widget_3_chart' style={{height: '350px'}}></div>
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export {ChartSalesPerPeriod}

function getChartOptions(height: number, categories, series): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-info')
  const lightColor = getCSSVariableValue('--bs-info-light')
  const colors = series.map((_, index) => getChartColor(index))
  return {
    series,
    chart: {
      fontFamily: 'inherit',
      type: 'line',
      height,
      toolbar: {
        show: false,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors,
    },
    xaxis: {
      type: 'datetime',
      categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },

      labels: {
        format: 'dd/MM/yyyy',
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: true,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return formatMonetaryToBr(val)
        },
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: true,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      theme: 'dark',
      style: {
        fontSize: '12px',
      },
      x: {
        format: 'dd/MM/yyyy',
      },
      y: {
        formatter: function (val) {
          return formatMonetaryToBr(val)
        },
      },
    },
    colors,
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeColors: baseColor,
      strokeWidth: 3,
    },
  }
}
